import React, { useState } from 'react';
import { Button, Form, Tag, Divider, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form/Form';
import { CategorySelectionModal } from './CategorySelectionModal';

interface Category {
  mainCategory: string;
  subCategory: string;
  productCategories: string[];
  categoryRootId: number;
  categoryPrimeId: number;
  categorySecondIds: number[];
}

interface CategoryManagerProps {
  form: FormInstance;
  profile_id: number | null;
  fieldName?: string;
  className?: string;
  readOnly?: boolean;
  /** Disables the Add button only; Edit/Delete on existing rows remain interactive. */
  disabled?: boolean;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  form,
  fieldName = 'category',
  className = '',
  readOnly = false,
  disabled = false,
  profile_id
}: CategoryManagerProps): React.ReactElement => {
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState<number | null>(null);

  // Subscribe via Form.useWatch so async parent `setFieldsValue` (e.g. edit-mode hydration after a
  // GET /deals/:id resolves) flows into render. Fall back to a synchronous `getFieldValue` read for
  // contexts where useWatch can't subscribe (e.g. unit tests that pass a partial form mock).
  const watched = Form.useWatch(fieldName, form);
  const fromForm = watched ?? form.getFieldValue?.(fieldName);
  const selectedCategories: Category[] = Array.isArray(fromForm) ? fromForm : [];

  const handleEditCategory = (index: number) => {
    setEditingCategoryIndex(index);
    setCategoryModalVisible(true);
  };

  const handleCancelEdit = () => {
    setCategoryModalVisible(false);
    setEditingCategoryIndex(null);
  };

  const handleDeleteCategory = (index: number) => {
    const updatedCategories = selectedCategories.filter((_, i) => i !== index);
    form.setFieldsValue({ [fieldName]: updatedCategories });
  };

  const handleAddCategories = (categories: Category[]) => {
    if (editingCategoryIndex !== null) {
      const updatedCategories = [...selectedCategories];
      updatedCategories[editingCategoryIndex] = categories[0];
      form.setFieldsValue({ [fieldName]: updatedCategories });
      setEditingCategoryIndex(null);
    } else {
      // add new categories to existing ones instead of replacing
      const updatedCategories = [...selectedCategories, ...categories];
      form.setFieldsValue({ [fieldName]: updatedCategories });
    }
    setCategoryModalVisible(false);
  };

  const renderSelectedCategories = () => {
    if (selectedCategories.length === 0) return <div className="text-sm text-gray-700 gap-2 font-bold"> - </div>;

    return (
      <div
        className={
          readOnly
            ? 'selected-categories-container'
            : 'selected-categories-container mb-4 border-t border-gray-200 pt-3 mt-3'
        }
      >
        {selectedCategories.map((category, index) => (
          <div key={index} className="category-item mb-3 mt-3">
            <div className="flex items-center mb-2">
              <div className="text-sm text-gray-700 gap-2 font-bold whitespace-nowrap">
                {category.mainCategory} <span className="text-gray-500"> {category.subCategory ? '→' : ''}</span>{' '}
                {category.subCategory}
              </div>
              {!readOnly && <div className="flex-1 h-px bg-gray-300 mx-2" />}
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditCategory(index)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Edit
                  </Button>
                  <Divider className="bg-gray-300" type="vertical" />
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteCategory(index)}
                    className="text-red-600 hover:text-red-700"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-wrap mb-2">
              {category.productCategories.slice(0, 3).map((productCategory: string, productIndex: number) => (
                <Tag key={productIndex} className="bg-gray-100 text-gray-700 border-gray-200 px-3 py-1 mt-1">
                  {productCategory}
                </Tag>
              ))}
              {category.productCategories.length > 3 && (
                <Tooltip
                  title={
                    <div>
                      {category.productCategories.slice(3).map((productCategory: string, index: number) => (
                        <div key={index} className="text-white">
                          {productCategory}
                        </div>
                      ))}
                    </div>
                  }
                  placement="top"
                >
                  <Tag className="bg-gray-100 text-gray-700 border-gray-200 rounded-full px-3 py-1 mt-1 cursor-pointer">
                    +{category.productCategories.length - 3}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={className}>
      {!readOnly && (
        <Button
          type="default"
          className="self-center mx-1"
          disabled={disabled}
          onClick={() => {
            setCategoryModalVisible(true);
          }}
        >
          <PlusOutlined className="text-base" />
          Add Categories
        </Button>
      )}
      {renderSelectedCategories()}

      <CategorySelectionModal
        open={categoryModalVisible}
        profile_id={profile_id}
        onCancel={handleCancelEdit}
        onAdd={handleAddCategories}
        editingCategory={editingCategoryIndex !== null ? selectedCategories[editingCategoryIndex] : null}
      />
    </div>
  );
};

export default CategoryManager;
